import TableRow from "@mui/material/TableRow";
import { useContext } from "react";
import styled from "styled-components";
import {
    PRODUCT_CATEGORY,
    PRODUCT_ORDER_STATUS,
    ROLE,
    SALE_ORDER_TYPE,
} from "../../../../data/enums";
import { AuthenticationContext } from "../../../../provider/AuthenticationProvider";
import { CategoryContainer, StyledTableCell } from "../../../../shared/components/CustomTable/Styled";
import Status from "../../../../shared/components/Status";
import { StyledNavLink } from "../../../../shared/styled/TableStyles";
import { color } from "../../../../shared/styles";
import { formatDateVi } from "../../../../utils/dateUtils";
import { formatCurrency } from "../../../../utils/format";
import { getStatusColorTheme } from "../statusTheme";
import {
    getOrderTypeLabel,
    getSaleOrderStatusLabel,
    getWorkshopLabel,
} from "../../../ReserveOrder/utils";
import { getOrderCategories } from "./orderCategories";
import DetailProgressCell from "./DetailProgressCell";
import { formatManufacturingOrderCodes } from "./manufacturingOrderCodes";

// Function to check if a date is older than 5 days
const isOlderThanFiveDays = (date: Date): boolean => {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 5;
};

export { getStatusColorTheme };

const getCategoryColorTheme = (category: string) => {
    switch (category) {
        case PRODUCT_CATEGORY.BARREL:
            return "SUCCESS";
        case PRODUCT_CATEGORY.VESSEL:
            return "WARNING";
        case PRODUCT_CATEGORY.VEHICLE:
            return "INFO";
        default:
            return "INFO";
    }
};

const StyledStatus = styled(Status)`
    flex-shrink: 0;
    flex: 0 1 auto;
    min-width: 0;
`;

export const ProductOrderRow = ({ item }: { item: any }) => {
    const {
        user: { role },
    } = useContext(AuthenticationContext);

    // Calculate price dynamically if item.price is 0 or undefined
    const calculateProductOrderPrice = () => {
        // If item.price is properly populated and not 0, use it
        if (item.order_price && item.order_price > 0) {
            return Number(item.order_price);
        }

        // Otherwise, calculate from products and materials
        let totalPrice = 0;

        // Calculate price from products
        if (item.products && item.products.length > 0) {
            totalPrice += item.products.reduce((sum: any, product: any) => {
                const productPrice = (product as any).product_price || product.price || 0;
                const quantity = (product as any).selectedQuantity || product.quantity || 0;
                return sum + (productPrice * quantity);
            }, 0);
        }

        // Calculate price from materials
        if (item.materials && item.materials.length > 0) {
            totalPrice += item.materials.reduce((sum: any, material: any) => {
                const materialPrice = (material as any).material_price || material.price || 0;
                const quantity = (material as any).selectedQuantity || material.quantity || 0;
                return sum + (materialPrice * quantity);
            }, 0);
        }

        return totalPrice;
    };

    const isReserve = item.order_type === SALE_ORDER_TYPE.RESERVE;
    const productOrderPrice = isReserve
        ? "—"
        : formatCurrency(calculateProductOrderPrice());

    // Check if the last_modified date is older than 5 days
    const lastModifiedDate = new Date(item.edited_at);
    const createdDate = new Date(item.created_at);
    const isOldOrder = isOlderThanFiveDays(lastModifiedDate);

    // Apply WARNING background color if the order is old
    const rowStyle = isOldOrder && ![PRODUCT_ORDER_STATUS.COMPLETED, PRODUCT_ORDER_STATUS.CANCELLED].includes(item.status)
        ? { backgroundColor: color.status.WARNING_LIGHT }
        : {};

    // Handle cases where customer might be null
    const hasCustomer = !isReserve && item.customer_name !== null;
    const customerName = isReserve
        ? `Nội bộ - ${getWorkshopLabel(item.workshop)}`
        : item.customer_name !== null
          ? item.customer_name
          : "Không có khách hàng";
    const customerId = item.customer_id || item.customer?.id || item.customer_uuid || null;
    const customerLink = customerId !== null ? `/customers/${customerId}` : "#";
    const categories = getOrderCategories(item);
    const manufacturingOrderCodeText = formatManufacturingOrderCodes(
        item.manufacturing_order_codes,
    );


    const isShowPrice = ![ROLE.FIRST_STORAGE_KEEPER, ROLE.SECOND_STORAGE_KEEPER].includes(role);

    return (
        <TableRow style={rowStyle}>
            <StyledTableCell align="left">
                <StyledNavLink to={`/product-orders/${item.id}`}>{item.order_name || item.name || item.order_code || item.code || ""}</StyledNavLink>
            </StyledTableCell>
            <StyledTableCell align="left">
                {isReserve ? "—" : item.po_number || "—"}
            </StyledTableCell>
            <StyledTableCell align="left">
                {getOrderTypeLabel(item.order_type)}
            </StyledTableCell>
            <StyledTableCell align="left">{manufacturingOrderCodeText}</StyledTableCell>
            <StyledTableCell align="left">{formatDateVi(createdDate)}</StyledTableCell>
            <StyledTableCell align="left">
                {isReserve ? (
                    customerName
                ) : hasCustomer ? (
                    <StyledNavLink to={customerLink}>{customerName}</StyledNavLink>
                ) : (
                    <span style={{ color: color.status.WARNING }}>{customerName}</span>
                )}
            </StyledTableCell>
            <StyledTableCell align="left">
                <CategoryContainer>
                    {categories.length > 0 ? (
                        categories.map((category: any) => (
                            <StyledStatus
                                key={category}
                                colorTheme={getCategoryColorTheme(category)}
                                text={category}
                            />
                        ))
                    ) : (
                        <StyledStatus
                            colorTheme="DISABLE"
                            text="Không có"
                        />
                    )}
                </CategoryContainer>
            </StyledTableCell>
            <StyledTableCell align="left">{formatDateVi(new Date(item.due_date))}</StyledTableCell>
            <StyledTableCell align="left">
                <Status
                    colorTheme={getStatusColorTheme(item.status)}
                    text={getSaleOrderStatusLabel(
                        item.status,
                        isReserve,
                    )}
                    width="124px"
                />
            </StyledTableCell>
            <StyledTableCell align="left">
                <DetailProgressCell
                    progress={item.detail_progress}
                    materialStatus={item.detail_status}
                />
            </StyledTableCell>
            {isShowPrice && (
                <StyledTableCell align="left">{productOrderPrice}</StyledTableCell>
            )}
            <StyledTableCell align="left">{formatDateVi(lastModifiedDate)}</StyledTableCell>
        </TableRow>
    );
};
